import type { Row } from "./api";
import {
  friendlyValue,
  resolveIntakeSchema,
  validateIntakeValue,
} from "./intake-values";

export const isTaxObject = (value: unknown): value is Row =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const owns = (value: object, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);

export function taxChoiceLabel(value: unknown): string {
  if (value === "PRESENT" || value === true || value === "YES") return "Yes";
  if (value === "ABSENT" || value === false || value === "NO") return "No";
  if (value === "UNKNOWN") return "Not sure";
  if (value === "CONFLICT") return "Conflicting information";
  if (value === null) return "Not provided";
  return friendlyValue(value);
}

/** Only an explicit field edit creates a supplied local answer. Rendering never invokes this helper. */
export function taxAnswerChange(nextValue: unknown): unknown {
  return nextValue === undefined
    ? undefined
    : { state: "KNOWN", value: nextValue };
}

export function taxAnswerView(value: unknown): {
  value: unknown;
  replacementRequired: boolean;
  notice: string;
} {
  if (!isTaxObject(value) || typeof value.state !== "string")
    return { value, replacementRequired: false, notice: "" };
  if (value.state === "KNOWN")
    return { value: value.value, replacementRequired: false, notice: "" };
  return {
    value: undefined,
    replacementRequired: true,
    notice:
      value.state === "CONFLICT"
        ? "The saved information conflicts. Keep it for follow-up or replace it when you have the correct information."
        : "This information has not been provided. The saved answer will stay unchanged until you replace it.",
  };
}

/** A visual nullable branch does not synthesize null, false, an empty object or an empty collection. */
export function taxNullableSchema(
  schema: Row,
  definitions?: Row,
): { schema: Row; nullable: boolean } | null {
  const resolved = resolveIntakeSchema(schema, definitions);
  if (!resolved) return null;
  const alternatives: Row[] | undefined =
    resolved.anyOf ??
    resolved.oneOf ??
    (Array.isArray(resolved.type)
      ? resolved.type.map((type: string) => ({ ...resolved, type }))
      : undefined);
  if (!alternatives)
    return { schema: resolved, nullable: resolved.type === "null" };
  const nonNull = alternatives.filter(
    (branch) => resolveIntakeSchema(branch, definitions)?.type !== "null",
  );
  if (nonNull.length !== 1 || nonNull.length === alternatives.length)
    return null;
  const child = resolveIntakeSchema(nonNull[0], definitions);
  if (!child) return null;
  const { anyOf: _anyOf, oneOf: _oneOf, type: _type, ...siblings } = resolved;
  return { schema: { ...siblings, ...child }, nullable: true };
}

/** A condition is unknown until every field it inspects has an explicit value. */
function conditionHasFacts(
  condition: Row,
  value: unknown,
  definitions?: Row,
  depth = 0,
): boolean {
  if (depth > 24 || !isTaxObject(condition)) return false;
  const resolved = resolveIntakeSchema(condition, definitions);
  if (!resolved) return false;
  if (
    value === "UNKNOWN" ||
    value === "CONFLICT" ||
    value === "PENDING_POSTCALC" ||
    (isTaxObject(value) &&
      typeof value.state === "string" &&
      value.state !== "KNOWN")
  )
    return false;
  const typeOf = (item: unknown) =>
    item === null ? "null" : Array.isArray(item) ? "array" : typeof item;
  const expectedTypes: string[] = resolved.type
    ? Array.isArray(resolved.type)
      ? resolved.type
      : [resolved.type]
    : owns(resolved, "const")
      ? [typeOf(resolved.const)]
      : Array.isArray(resolved.enum)
        ? [...new Set(resolved.enum.map(typeOf))]
        : [];
  if (
    expectedTypes.length &&
    !expectedTypes.some((type) =>
      type === "integer" ? Number.isSafeInteger(value) : typeOf(value) === type,
    )
  )
    return false;
  const keys = [
    ...new Set([
      ...Object.keys(resolved.properties ?? {}),
      ...(resolved.required ?? []),
    ]),
  ];
  if (
    keys.length &&
    (!isTaxObject(value) ||
      keys.some((key) => !owns(value, key) || value[key] === undefined))
  )
    return false;
  if (
    isTaxObject(value) &&
    Object.entries(resolved.properties ?? {}).some(
      ([key, shape]) =>
        !conditionHasFacts(shape as Row, value[key], definitions, depth + 1),
    )
  )
    return false;
  if (
    ["anyOf", "oneOf", "allOf"].some(
      (key) =>
        Array.isArray(resolved[key]) &&
        resolved[key].some(
          (branch: Row) =>
            !conditionHasFacts(branch, value, definitions, depth + 1),
        ),
    )
  )
    return false;
  if (
    resolved.not &&
    !conditionHasFacts(resolved.not, value, definitions, depth + 1)
  )
    return false;
  return value !== undefined;
}

/** Selects declared display branches only. Does not validate eligibility, calculate or delete hidden values. */
export function taxVisibleObjectSchema(
  schema: Row,
  value: unknown,
  definitions?: Row,
  depth = 0,
): Row | null {
  if (depth > 24) return null;
  const resolved = resolveIntakeSchema(schema, definitions);
  if (!resolved) return null;
  const output: Row = {
    ...resolved,
    properties: { ...(resolved.properties ?? {}) },
    required: [...(resolved.required ?? [])],
  };
  const add = (branch: Row) => {
    const selected = taxVisibleObjectSchema(
      branch,
      value,
      definitions,
      depth + 1,
    );
    if (!selected) return;
    for (const [key, child] of Object.entries(selected.properties)) {
      const prior = output.properties[key];
      output.properties[key] =
        isTaxObject(prior) && isTaxObject(child)
          ? { ...prior, ...child, allOf: [prior, child] }
          : child;
    }
    output.required = [...new Set([...output.required, ...selected.required])];
  };
  for (const branch of resolved.allOf ?? [])
    if (isTaxObject(branch)) add(branch);
  if (
    isTaxObject(resolved.if) &&
    conditionHasFacts(resolved.if, value, definitions)
  ) {
    const matched = validateIntakeValue(value, resolved.if, definitions).valid;
    const branch = matched ? resolved.then : resolved.else;
    if (isTaxObject(branch)) add(branch);
  }
  return output;
}

/** Update exactly one supplied field while retaining hidden and unrelated saved information. */
export function taxObjectChange(
  value: unknown,
  key: string,
  next: unknown,
): Row {
  const result: Row = isTaxObject(value) ? { ...value } : {};
  if (next === undefined) delete result[key];
  else
    Object.defineProperty(result, key, {
      value: next,
      writable: true,
      enumerable: true,
      configurable: true,
    });
  return result;
}

const designeeFields = ["designee_name", "designee_phone", "designee_pin"];
/** Presentation-only dependency for the explicit designee choice. Unanswered and invalid choices stay open. */
export function taxDesigneeDetails(
  schema: Row,
  value: unknown,
  definitions?: Row,
): {
  hideDetails: boolean;
  missingNullableFields: string[];
  hasRetainedDetails: boolean;
} {
  const resolved = resolveIntakeSchema(schema, definitions);
  const properties = resolved?.properties;
  const matches =
    isTaxObject(properties) &&
    isTaxObject(properties.designee_choice) &&
    designeeFields.every((key) => isTaxObject(properties[key]));
  const hideDetails = Boolean(
    matches &&
    isTaxObject(value) &&
    value.designee_choice === "NO" &&
    validateIntakeValue(
      value.designee_choice,
      properties.designee_choice,
      definitions,
    ).valid,
  );
  if (!hideDetails || !isTaxObject(value))
    return {
      hideDetails: false,
      missingNullableFields: [],
      hasRetainedDetails: false,
    };
  return {
    hideDetails: true,
    missingNullableFields: designeeFields.filter(
      (key) =>
        resolved?.required?.includes(key) &&
        (!owns(value, key) || value[key] === undefined) &&
        validateIntakeValue(null, properties[key], definitions).valid,
    ),
    hasRetainedDetails: designeeFields.some(
      (key) =>
        owns(value, key) && value[key] !== undefined && value[key] !== null,
    ),
  };
}

/** Called only by the explicit confirmation button; does not overwrite even blank existing details. */
export function confirmTaxDesigneeDetails(
  value: unknown,
  schema: Row,
  definitions?: Row,
): unknown {
  const view = taxDesigneeDetails(schema, value, definitions);
  if (
    !view.hideDetails ||
    !view.missingNullableFields.length ||
    !isTaxObject(value)
  )
    return value;
  const confirmed = { ...value };
  for (const key of view.missingNullableFields) confirmed[key] = null;
  return confirmed;
}

/** Living-taxpayer year-end display only; not a substitute for provision-specific age or admission tests.
 * IRS 2025 Form 1040 instructions: a Jan 1, 1961 birthday is age 65 at the end of 2025.
 * https://www.irs.gov/instructions/i1040gi and https://www.irs.gov/publications/p501
 * Matches the local S1-AGE-DISPLAY-1.0.0 convention; no deceased-taxpayer rule is implemented.
 */
export function deriveTaxYearAge(
  dateOfBirth: unknown,
  year = 2025,
): number | undefined {
  if (
    !Number.isInteger(year) ||
    year < 1 ||
    year > 9999 ||
    typeof dateOfBirth !== "string"
  )
    return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOfBirth);
  if (!match) return undefined;
  const birthYear = Number(match[1]),
    month = Number(match[2]),
    day = Number(match[3]);
  const leap =
    birthYear % 4 === 0 && (birthYear % 100 !== 0 || birthYear % 400 === 0);
  const monthDays = [
    31,
    leap ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  if (
    birthYear < 1 ||
    birthYear > year ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > monthDays[month - 1]
  )
    return undefined;
  const age = year - birthYear + (month === 1 && day === 1 ? 1 : 0);
  return age >= 0 && age <= 150 ? age : undefined;
}

/** Returns a new local value only when its derived age needs updating; never changes a supplied DOB. */
export function normalizeTaxAge(value: unknown, year = 2025): unknown {
  if (!isTaxObject(value)) return value;
  if (typeof value.state === "string") {
    if (value.state !== "KNOWN") return value;
    const normalized = normalizeTaxAge(value.value, year);
    return normalized === value.value ? value : { ...value, value: normalized };
  }
  const key = `age_for_${year}`;
  if (!owns(value, "date_of_birth") && !owns(value, key)) return value;
  const age = deriveTaxYearAge(value.date_of_birth, year);
  if (age === undefined && !owns(value, key)) return value;
  if (age !== undefined && value[key] === age) return value;
  const normalized = { ...value };
  if (age === undefined) delete normalized[key];
  else normalized[key] = age;
  return normalized;
}
